/**
 * WorkflowStageChip - Indicador compacto da etapa atual da esteira
 *
 * Exibido no cabeçalho do registro (modal do negócio). Ao passar o mouse
 * mostra os próximos passos configurados; ao clicar rola até a trilha
 * completa na aba Resumo.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-31 v1.0.0 - Criação inicial
 */

import { Route } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useWorkflowModule } from '@/hooks/useWorkflowModule';
import { useWorkflowInstanceTimeline } from '@/hooks/useWorkflowInstanceTimeline';

interface WorkflowStageChipProps {
  rootEntityType: string;
  rootEntityId: string;
}

export function WorkflowStageChip({ rootEntityType, rootEntityId }: WorkflowStageChipProps) {
  const { isEnabled } = useWorkflowModule();
  const { progresso } = useWorkflowInstanceTimeline({
    rootEntityType,
    rootEntityId,
    enabled: isEnabled,
  });

  if (!isEnabled || !progresso?.etapa_atual) return null;

  const proximos = progresso.proximos_passos.filter((p) => !p.is_regression);

  // Rola até a trilha renderizada na aba Resumo
  const irParaTrilha = () => {
    document
      .getElementById('workflow-progress-trail')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" onClick={irParaTrilha} className="shrink-0">
          <Badge
            variant="outline"
            className="h-5 max-w-[180px] gap-1 truncate px-1.5 py-0.5 text-[10px] text-muted-foreground"
          >
            <Route className="h-3 w-3" />
            <span className="truncate">{progresso.etapa_atual.label}</span>
          </Badge>
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[280px]">
        <p className="text-xs font-medium">
          Esteira: {progresso.definicao?.nome ?? 'Processo'}
        </p>
        <p className="text-xs text-muted-foreground">Etapa atual: {progresso.etapa_atual.label}</p>
        {proximos.length > 0 && (
          <p className="mt-1 text-xs text-muted-foreground">
            Próximos: {proximos.map((p) => p.label).join(' · ')}
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
